import Card from "@/components/Card";
import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listLokasiKuliah } from "@/constants";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import {
  getDataStep,
  getListDataPilihanProdi,
  getListProdiPerPeriode,
  getListUktPerProdi,
} from "@/services/getData";
import { DataPilihanProdi, LokasiKuliah, SysProgramStudi } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  ChevronRight,
  GraduationCap,
  Info,
  Layers,
  LayoutList,
  Loader2,
  Save,
} from "lucide-react"; // Gunakan Lucide icons
import { Fragment, useEffect, useState } from "react";
import toast from "react-hot-toast";
import DataSekolah from "./partial/DataSekolah";
import KetentuanProdi from "./partial/KetentuanProdi";
import ListProdi from "./partial/ListProdi";
import ListUkt from "./partial/ListUkt";

// ... (Type definitions tetap sama) ...
type ProdiSelection = {
  uk_id: string;
  jenjang: string;
  pilihan_lokasi_kuliah: LokasiKuliah | null;
  pilihan_prodi_ke: number;
  is_vokasi: boolean;
  kelas_kuliah: LokasiKuliah[];
};

type PilihanProdiState = {
  id_data_pendaftaran: string;
  jumlah_pilihan_prodi: string;
  list_prodi: ProdiSelection[];
};

type OptionProdi = {
  label: string;
  value: string;
  unitkerjaGroupKey: string;
  jenjang: string;
  isActive: boolean;
  isKelasDenpasar: boolean;
  isVokasi: boolean;
  kelasKuliah: LokasiKuliah[];
};

const saveProdi = async (form: PilihanProdiState) => {
  const response = await api.patch(
    "/siswa/penerimaan/smbjm/pilihan-prodi/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function Prodi({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();
  const { timePassedActive } = useTimePeriode();

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  const [form, setForm] = useState<PilihanProdiState>({
    id_data_pendaftaran: idDataPendaftaran,
    jumlah_pilihan_prodi: "",
    list_prodi: [],
  });
  const [openListProdi, setOpenListProdi] = useState(
    Array(Number(form.jumlah_pilihan_prodi)).fill(false),
  );
  const [activeMenu, setActiveMenu] = useState(1);

  const { data: dataProdi, isPending: isPendingDataProdi } = useQuery({
    queryKey: ["listProdi"],
    queryFn: () => getListProdiPerPeriode(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  const { data: dataPilihanProdi } = useQuery({
    queryKey: ["listDataPilihanProdi"],
    queryFn: () =>
      getListDataPilihanProdi(sessionPeriode?.id_sys_periode || ""),
  });

  const ukIds =
    form.list_prodi.length > 0
      ? form.list_prodi.map((prodi) => prodi?.uk_id)
      : [];

  const { data: dataUkt, isPending: isPendingDataUkt } = useQuery({
    queryKey: ["listUkt", ukIds, form.list_prodi],
    queryFn: () => getListUktPerProdi(ukIds),
    enabled: !!ukIds,
  });

  const optionsProdi = dataProdi?.list_prodi?.map(
    (sysProdi: SysProgramStudi) => ({
      label: sysProdi?.unitkerja?.nama,
      value: `${sysProdi?.uk_id}`,
      unitkerjaGroupKey: sysProdi?.unitkerja?.unitkerja_group_key,
      jenjang: sysProdi?.unitkerja?.uk_program,
      isActive: sysProdi?.unitkerja?.is_active,
      isVokasi: sysProdi?.unitkerja?.is_vokasi,
      kelasKuliah: sysProdi?.unitkerja?.kelas_kuliah,
    }),
  );
  const filteredDataProdi = optionsProdi?.filter((option: OptionProdi) => {
    return option?.unitkerjaGroupKey == "5" && option.isActive;
  });

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveProdi,
    error,
    reset,
  } = useMutation({
    mutationFn: saveProdi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(
        data?.message || "Pilihan program studi berhasil disimpan.",
      );
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan saat menyimpan data.";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  useEffect(() => {
    if (!dataPilihanProdi) return;

    setForm((prev) => ({
      ...prev,
      jumlah_pilihan_prodi: `${dataPilihanProdi?.length}`,
      list_prodi: dataPilihanProdi?.map((item: DataPilihanProdi) => {
        return {
          uk_id: `${item?.uk_id}`,
          jenjang: item.unitkerja?.uk_program,
          pilihan_lokasi_kuliah: item.pilihan_lokasi_kuliah,
          pilihan_prodi_ke: item.urutan_program_studi,
          is_vokasi: item.unitkerja?.is_vokasi,
          kelas_kuliah: item.unitkerja?.kelas_kuliah,
        };
      }),
    }));
  }, [dataPilihanProdi]);

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  // --- Helper Components ---
  const NavButton = ({ id, label, icon: Icon }: any) => (
    <div
      onClick={() => setActiveMenu(id)}
      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
        activeMenu === id
          ? "bg-primary text-white shadow-md"
          : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium text-sm">{label}</span>
      {activeMenu === id && <ChevronRight className="w-4 h-4 ml-auto" />}
    </div>
  );

  return (
    <Fragment key="prodi-smbjm-prestasi">
      <div className="flex flex-col lg:flex-row gap-6 items-start flex-1">
        {/* --- LEFT COLUMN: NAVIGATION & INFO --- */}
        <div className="w-full lg:w-1/2 space-y-6 lg:sticky lg:top-6">
          <Card className="p-4 space-y-2">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 px-3">
              Menu Informasi
            </h3>
            <NavButton id={1} label="Daftar Program Studi" icon={LayoutList} />
            {/* <NavButton id={2} label="Ketentuan Memilih Prodi" icon={BookOpen} /> */}
          </Card>

          {/* Dynamic Content based on Active Menu */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm max-h-screen overflow-y-auto custom-scrollbar">
            {activeMenu === 1 && (
              <ListProdi dataProdi={dataProdi} isPending={isPendingDataProdi} />
            )}
            {activeMenu === 2 && <KetentuanProdi />}
          </div>
        </div>

        {/* --- RIGHT COLUMN: FORMS & ACTIONS --- */}
        <div className="w-full lg:w-1/2 space-y-6 grid grid-cols-1">
          {/* 1. DATA SEKOLAH */}
          <DataSekolah />

          {/* 2. FORM PILIHAN PRODI */}
          {isPendingDataProdi /* === SKELETON LOADING === */ ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col gap-6 animate-pulse">
              {/* Header Skeleton */}
              <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="skeleton w-10 h-10 rounded-lg shrink-0" />
                <div className="space-y-2 w-full max-w-sm">
                  <div className="skeleton h-5 w-[200px] rounded-md" />
                  <div className="skeleton h-4 w-[300px] rounded-md" />
                </div>
              </div>

              {/* Radio Group Skeleton (Jumlah Pilihan Prodi) */}
              <div className="space-y-3">
                <div className="skeleton h-4 w-[150px] rounded-md" />
                <div className="grid lg:grid-cols-4 grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="skeleton h-14 w-full rounded-md" />
                  ))}
                </div>
              </div>

              {/* List Prodi Inputs Skeleton (Simulasi 2 Pilihan) */}
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="grid grid-cols-1 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-3"
                  >
                    {/* Badge Pilihan */}
                    <div className="skeleton h-5 w-20 rounded-md mb-1" />

                    {/* Combobox Prodi */}
                    <div className="space-y-1">
                      <div className="skeleton h-3 w-24 rounded-md" />
                      <div className="skeleton h-10 w-full rounded-md" />
                    </div>

                    {/* Select Lokasi (Disimulasikan sesekali muncul) */}
                    <div className="space-y-1">
                      <div className="skeleton h-3 w-20 rounded-md" />
                      <div className="skeleton h-10 w-full rounded-md" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Button Skeleton */}
              <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                <div className="skeleton h-10 w-[150px] rounded-md" />
              </div>
            </div>
          ) : dataStep && dataStep?.is_confirm_jurusan_sekolah ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-600 dark:text-blue-400">
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                      Memilih Program Studi
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Tentukan prioritas program studi yang Anda minati.
                    </p>
                  </div>
                </div>

                {/* Info Alert based on selection */}
                {form.jumlah_pilihan_prodi &&
                  form.jumlah_pilihan_prodi != "0" && (
                    <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <AlertTitle className="text-blue-800 dark:text-blue-300 font-semibold text-sm">
                        Ketentuan {form.jumlah_pilihan_prodi} Pilihan:
                      </AlertTitle>
                      <AlertDescription className="text-blue-700 dark:text-blue-400 text-xs mt-1">
                        {form.jumlah_pilihan_prodi == "1" &&
                          "Bebas memilih di program apapun."}
                        {form.jumlah_pilihan_prodi == "2" &&
                          "Bebas memilih di program apapun."}
                        {form.jumlah_pilihan_prodi == "3" &&
                          "2 Program Akademik & 1 Vokasi ATAU 1 Akademik & 2 Vokasi."}
                        {form.jumlah_pilihan_prodi == "4" &&
                          "2 Program Akademik & 2 Vokasi (Min. 1 D3)."}
                      </AlertDescription>
                    </Alert>
                  )}

                {/* Jumlah Pilihan Selector */}
                <div className="space-y-3">
                  <Label className="font-semibold">Jumlah Pilihan Prodi</Label>
                  <RadioGroup
                    required
                    defaultValue="comfortable"
                    onValueChange={(value: string) => {
                      setForm((prev) => ({
                        ...prev,
                        jumlah_pilihan_prodi: value,
                        list_prodi: [],
                      }));
                      reset();
                    }}
                    className="grid grid-cols-2 gap-4"
                  >
                    {["1", "2"].map((value, index) => (
                      <div key={index}>
                        <RadioGroupItem
                          value={value}
                          id={`opt-${value}`}
                          className="peer sr-only"
                          checked={form.jumlah_pilihan_prodi === value}
                        />
                        <Label
                          htmlFor={`opt-${value}`}
                          className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-900/20 [&:has([data-state=checked])]:border-primary cursor-pointer transition-all text-center"
                        >
                          <span className="text-lg font-bold">{value}</span>
                          <span className="text-[10px] uppercase text-muted-foreground">
                            Pilihan
                          </span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* List Prodi Inputs */}
                <div className="space-y-4">
                  {form.jumlah_pilihan_prodi &&
                    Array(Number(form.jumlah_pilihan_prodi))
                      .fill(null)
                      .map((_, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 overflow-auto p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-3 animate-in fade-in slide-in-from-top-2"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              variant="outline"
                              className="bg-white dark:bg-slate-800"
                            >
                              Pilihan {index + 1}
                            </Badge>
                          </div>

                          <div className="grid md:grid-cols-1 gap-4">
                            <div className="space-y-1">
                              <Label className="text-xs">Program Studi</Label>
                              <Combobox
                                open={openListProdi[index]}
                                setOpen={(isOpen) =>
                                  setOpenListProdi((prev) => {
                                    const newOpenList = [...prev];
                                    newOpenList[index] = isOpen;
                                    return newOpenList;
                                  })
                                }
                                value={form.list_prodi[index]?.uk_id || ""}
                                setValue={(value) => {
                                  const selectedProdi = filteredDataProdi?.find(
                                    (item: OptionProdi) => item.value === value,
                                  );
                                  const isDuplicate = form.list_prodi.some(
                                    (prodi) => prodi?.uk_id === value,
                                  );

                                  if (isDuplicate) {
                                    toast.error(
                                      "Prodi ini sudah Anda pilih sebelumnya.",
                                    );
                                    return;
                                  }

                                  setForm((prev) => {
                                    const newListProdi = [...prev.list_prodi];
                                    newListProdi[index] = {
                                      uk_id: value,
                                      jenjang: selectedProdi?.jenjang,
                                      pilihan_lokasi_kuliah: null,
                                      pilihan_prodi_ke: index + 1,
                                      is_vokasi: selectedProdi?.isVokasi,
                                      kelas_kuliah: selectedProdi?.kelasKuliah,
                                    };
                                    return {
                                      ...prev,
                                      list_prodi: newListProdi,
                                    };
                                  });
                                }}
                                options={filteredDataProdi}
                                placeholder="Cari program studi..."
                              />
                            </div>

                            {form.list_prodi[index]?.kelas_kuliah &&
                              form.list_prodi[index]?.kelas_kuliah?.length >
                                1 && (
                                <div className="space-y-1">
                                  <Label className="text-xs">
                                    Lokasi Kuliah
                                  </Label>
                                  <Select
                                    value={
                                      form.list_prodi[index]
                                        ?.pilihan_lokasi_kuliah || ""
                                    }
                                    onValueChange={(value) => {
                                      setForm((prev) => {
                                        const newListProdi = [
                                          ...prev.list_prodi,
                                        ];
                                        newListProdi[index] = {
                                          ...newListProdi[index],
                                          pilihan_lokasi_kuliah:
                                            value.toLowerCase() as LokasiKuliah,
                                        };
                                        return {
                                          ...prev,
                                          list_prodi: newListProdi,
                                        };
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="bg-white dark:bg-slate-900">
                                      <SelectValue placeholder="Pilih Lokasi" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {listLokasiKuliah.map((value, idx) => (
                                        <SelectItem
                                          key={idx}
                                          value={value.toLowerCase()}
                                        >
                                          {value}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {error?.response?.data?.errors?.[
                                    `list_prodi.${index}.pilihan_lokasi_kuliah`
                                  ] && (
                                    <InputError text="Pilihan lokasi kuliah wajib diisi." />
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      ))}

                  {error && error?.response?.data?.message && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>
                        {error.response.data.message}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {!isDisableInput && !isPendingDataProdi && (
                  <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                    <Button
                      type="submit"
                      disabled={isPendingSaveProdi}
                      className="min-w-[150px]"
                    >
                      {isPendingSaveProdi ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Simpan Pilihan
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </form>
            </div>
          ) : null}

          {/* 3. INFO UKT & IPI */}
          {dataStep?.is_confirm_jurusan_sekolah ? (
            <div className="space-y-4">
              {isPendingDataUkt ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              ) : (
                dataUkt && (
                  <>
                    <ListUkt dataUkt={dataUkt} />

                    <Alert className="bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100">
                      <Layers className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      <AlertTitle className="font-bold text-emerald-800 dark:text-emerald-300">
                        Tentang IPI (Iuran Pengembangan Institusi)
                      </AlertTitle>
                      <AlertDescription className="text-sm mt-1 text-emerald-800/80 dark:text-emerald-200/80 leading-relaxed">
                        Tarif IPI digunakan untuk pengembangan sumber daya
                        manusia, sarana, dan prasarana di lingkungan Universitas
                        Pendidikan Ganesha.
                      </AlertDescription>
                    </Alert>
                  </>
                )
              )}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    </Fragment>
  );
}
